import { ConfigStateService } from '@abp/ng.core';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from 'src/environments/environment';
import { HttpClient } from '@angular/common/http';
import { PhraseTypeDto } from 'src/core/models/conversation/phrase-type/phrase-type.dto';
import { NERTemplateDto } from 'src/core/models/conversation/phrase-type/ner-template.dto';
import { RedactionResponseDto } from 'src/core/models/conversation/phrase-type/redaction-response.dto';
import { CallLanguageCodeDto } from 'src/core/models/call/call-language-code.dto';
import { CustomPhraseTypeDto } from 'src/core/models/conversation/phrase-type/custom-phrase-type.dto';

@Injectable({
  providedIn: 'root',
})
export class PhraseTypeSettingsService {
  constructor(private config: ConfigStateService, private http: HttpClient) {}

  public get redactionEnabled(): boolean {
    return this.config.getSetting('Global.DynamicDataMaskingEnabled').toLowerCase() === 'true'
      ? true
      : false;
  }

  public get staticRedactionEnabled(): boolean {
    return this.config.getSetting('Global.DynamicDataMaskingEnabled').toLowerCase() == 'true' &&
      this.config.getSetting('PhraseType.RedactionType') == 'Conversation.RedactionType.Static'
      ? true
      : false;
  }

  public get dynamicRedactionEnabled(): boolean {
    return this.config.getSetting('Global.DynamicDataMaskingEnabled').toLowerCase() == 'true' &&
      this.config.getSetting('PhraseType.RedactionType') == 'Conversation.RedactionType.Dynamic'
      ? true
      : false;
  }

  public get dynamicRedactionWithPerformInternalOperationsWithMaskedDataEnabled(): boolean {
    let redactionEnabled =
      this.config.getSetting('Global.DynamicDataMaskingEnabled').toLowerCase() == 'true' &&
      this.config.getSetting('PhraseType.RedactionType') == 'Conversation.RedactionType.Dynamic'
        ? true
        : false;

    let performInternalOperationsWithMaskedDataEnabled =
      this.config
        .getSetting('Redaction.PerformInternalOperationsWithMaskedDataEnabled')
        .toLowerCase() === 'true'
        ? true
        : false;
    return redactionEnabled && performInternalOperationsWithMaskedDataEnabled;
  }

  public get dynamicRedactionWithPerformLLMEnabled(): boolean {
    let redactionEnabled =
      this.config.getSetting('Global.DynamicDataMaskingEnabled').toLowerCase() == 'true' &&
      this.config.getSetting('PhraseType.RedactionType') == 'Conversation.RedactionType.Dynamic'
        ? true
        : false;

    let performLLMWithMaskedDataEnabled =
      this.config.getSetting('Redaction.PerformLLMWithMaskedDataEnabled').toLowerCase() === 'true'
        ? true
        : false;
    return redactionEnabled && performLLMWithMaskedDataEnabled;
  }

  getNERTemplates(): Observable<NERTemplateDto[]> {
    const url = this.getApiUrl() + '/recognition';
    return this.http.get(url) as Observable<NERTemplateDto[]>;
  }

  updateNEREnability(templateIds: number[]) {
    const url = this.getApiUrl() + '/recognition-enability';
    return this.http.put(url, templateIds);
  }

  getLanguages(checkRedactionEnabled): Observable<CallLanguageCodeDto[]> {
    const url = this.getApiUrl() + '/languages/' + checkRedactionEnabled;

    return this.http.get(url) as Observable<CallLanguageCodeDto[]>;
  }

  getRedactionTemplates(): Observable<RedactionResponseDto> {
    const url = this.getApiUrl() + '/redaction';

    return this.http.get(url) as Observable<RedactionResponseDto>;
  }

  getByName(name: string): Observable<PhraseTypeDto> {
    const url = this.getApiUrl() + '/by-name?name=' + encodeURIComponent(name);

    return this.http.get(url) as Observable<PhraseTypeDto>;
  }

  getCustomPhraseTypeByName(name: string): Observable<CustomPhraseTypeDto> {
    const url = this.getApiUrl() + '/custom/by-name?name=' + encodeURIComponent(name);

    return this.http.get(url) as Observable<CustomPhraseTypeDto>;
  }

  getCustomPhraseTypes(): Observable<CustomPhraseTypeDto[]> {
    const url = this.getApiUrl() + '/custom';

    return this.http.get(url) as Observable<CustomPhraseTypeDto[]>;
  }

  createCustomPhraseTypes(phraseType: CustomPhraseTypeDto): Observable<CustomPhraseTypeDto> {
    const url = this.getApiUrl() + '/custom';

    return this.http.post(url, phraseType) as Observable<CustomPhraseTypeDto>;
  }

  updateCustomPhraseType(dto: CustomPhraseTypeDto): Observable<CustomPhraseTypeDto> {
    const url = this.getApiUrl() + '/custom/' + dto.id;

    return this.http.put<CustomPhraseTypeDto>(url, dto);
  }

  deleteCustomPhraseType(id: number): Observable<boolean> {
    const url = this.getApiUrl() + '/custom/' + id;

    return this.http.delete<boolean>(url);
  }

  checkRegexDetailSyntaxValid(pattern: string): Observable<string> {
    const url =
      this.getApiUrl() +
      '/custom/regex-details/syntax-valid?pattern=' +
      encodeURIComponent(pattern);

    return this.http.get(url) as Observable<string>;
  }

  private getApiUrl(): string {
    const apiBase = environment.apis.default.url;

    return apiBase.length > 0 ? apiBase + '/' + PhraseTypeDto.apiUrl : PhraseTypeDto.apiUrl;
  }
}
